import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useContext,
  forwardRef,
} from "react";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  updateEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import { Paper } from "@mui/material";
import { useSnackbar } from "notistack";
import { ulid } from "ulid";
import "reactflow/dist/style.css";
import "./flowbuilderpannel.css";
import NodesSection from "./NodesSection";
import CustomNode from "./CustomNode";
import ButtonEdge from "./ButtonEdge";

import NodeModalReadCSV from "./flow-builder-form/read/NodeModalReadCSV";
import NodeModalReadPostgres from "./flow-builder-form/read/NodeModalReadPostgres";
import NodeModalReadBenchlingPostgres from "./flow-builder-form/read/NodeModalReadBenchlingPostgres";
import NodeModalReadOracle from "./flow-builder-form/read/NodeModalReadOracle";

import NodeModalInstrument from "./flow-builder-form/instrument/NodeModalInstrument";
import NodeModalSqlConnector from "./flow-builder-form/power-bi/NodeModalSqlConnector";

import NodePropertiesSort from "./flow-builder-form/transform/NodeProperties_Sort";
import NodePropertiesFilter from "./flow-builder-form/transform/NodeProperties_Filter";
import NodeModalExecuteSql from "./flow-builder-form/transform/NodeModal_ExecuteSQL";
import NodeModalJoin from "./flow-builder-form/transform/NodeModal_Join";
import NodeModalUnion from "./flow-builder-form/transform/NodeModal_Union";
import NodeModalAggregate from "./flow-builder-form/transform/NodeModal_Aggregate";
import NodeModalCoalease from "./flow-builder-form/transform/NodeModal_Coalease";

import NodeModalWrite from "./flow-builder-form/write/NodeModal_Write";
import NodeModalWriteToBenchling from "./flow-builder-form/write/NodeModal_WriteToBenchling";
import NodeModal_WriteToPostgres from "./flow-builder-form/write/NodeModal_WriteToPostgres";
// import NodeModalUpdateToBenchling from "./flow-builder-form/write/NodeModal_UpdateToBenchling";

import NodeModalEmail from "./flow-builder-form/others/NodeModal_Email";

import NodeModalSource from "./flow-builder-form/quality/NodeModal_Source";
import NodeModalProfiling from "./flow-builder-form/quality/NodeModal_Profiling";
import NodeModalDataValidation from "./flow-builder-form/quality/NodeModal_DataValidation";
import NodeModalIngestionWrite from "./flow-builder-form/write/NodeModal_IngestionWrite";

import NodeModalPreprocessInstrumentFile from "./flow-builder-form/ingestion/NodeModalPreprocessInstrumentFile";
import { WorkflowContext } from "../../contexts/WorkflowProvider";
import { createDataProcessorApi } from "../../services/DataProcessorApi";
// import DataLineageGraph from "./data-lineage/DataLineageGraph";
import { useAxios } from "../../contexts/AxiosProvider";

const edgeTypes = {
  buttonedge: ButtonEdge,
};

const getId = () => ulid();

const ReactFlowPannel = forwardRef((props, ref) => {
  const workflow = props.workflow;
  const userData = props.userData;
  const nodeTypes = useMemo(() => ({ editableNode: CustomNode }), []);
  const axios = useAxios();
  const { isDisabled, setIsDisabled } = useContext(WorkflowContext);
  const { enqueueSnackbar } = useSnackbar();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [nodeName, setNodeName] = useState("");
  const [nodeId, setNodeId] = useState("");

  const [sortOpen, setSortOpen] = useState(false);
  const handleSortOpen = () => setSortOpen(true);
  const handleSortClose = () => setSortOpen(false);

  const [filterOpen, setFilterOpen] = useState(false);
  const handleFilterOpen = () => setFilterOpen(true);
  const handleFilterClose = () => setFilterOpen(false);

  const [unionOpen, setUnionOpen] = useState(false);
  const handleUnionOpen = () => setUnionOpen(true);
  const handleUnionClose = () => setUnionOpen(false);

  const [joinOpen, setJoinOpen] = useState(false);
  const handleJoinOpen = () => setJoinOpen(true);
  const handleJoinClose = () => setJoinOpen(false);

  const [readOpen, setReadOpen] = useState(false);
  const handleReadOpen = () => setReadOpen(true);
  const handleReadClose = () => setReadOpen(false);

  const [readCsvOpen, setReadCsvOpen] = useState(false);
  const handleReadCsvOpen = () => setReadCsvOpen(true);
  const handleReadCsvClose = () => setReadCsvOpen(false);

  const [preprocessInstrumentFileOpen, setPreprocessInstrumentFileOpen] =
    useState(false);
  const handlePreprocessInstrumentFileOpen = () =>
    setPreprocessInstrumentFileOpen(true);
  const handlePreprocessInstrumentFileClose = () =>
    setPreprocessInstrumentFileOpen(false);

  const [instrumentOpen, setInstrumentOpen] = useState(false);
  const handleInstrumentOpen = () => setInstrumentOpen(true);
  const handleInstrumentClose = () => setInstrumentOpen(false);

  const [sqlConnectorOpen, setSqlConnectorOpen] = useState(false);
  const handleSqlConnectorOpen = () => setSqlConnectorOpen(true);
  const handleSqlConnectorClose = () => setSqlConnectorOpen(false);

  const [writePostgresOpen, setWritePostgresOpen] = useState(false);
  const handleWritePostgresOpen = () => setWritePostgresOpen(true);
  const handleWritePostgresClose = () => setWritePostgresOpen(false);

  const [copyDocOpen, setCopyDocOpen] = useState(false);
  const handleCopyDocOpen = () => setCopyDocOpen(true);
  const handleCopyDocClose = () => setCopyDocOpen(false);

  const [readPublishedDataOpen, setReadPublishedDataOpen] = useState(false);
  const handleReadPublishedDataOpen = () => setReadPublishedDataOpen(true);
  const handleReadPublishedDataClose = () => setReadPublishedDataOpen(false);

  const [FileUploaderOpen, setFileUploaderOpen] = useState(false);
  const handleFileUploaderOpen = () => setFileUploaderOpen(true);
  const handleFileUploaderClose = () => setFileUploaderOpen(false);

  const [readStreamingOpen, setReadStreamingOpen] = useState(false);
  const handleReadStreamingOpen = () => setReadStreamingOpen(true);
  const handleReadStreamingClose = () => setReadStreamingOpen(false);

  const [readDelimitedOpen, setReadDelimitedOpen] = useState(false);
  const handleReadDelimitedOpen = () => setReadDelimitedOpen(true);
  const handleReadDelimitedClose = () => setReadDelimitedOpen(false);

  const [readOracleOpen, setReadOracleOpen] = useState(false);
  const handleReadOracleOpen = () => setReadOracleOpen(true);
  const handleReadOracleClose = () => setReadOracleOpen(false);

  const [readPostgresOpen, setReadPostgresOpen] = useState(false);
  const handleReadPostgresOpen = () => setReadPostgresOpen(true);
  const handleReadPostgresClose = () => setReadPostgresOpen(false);

  const [readBenchlingPostgresOpen, setReadBenchlingPostgresOpen] =
    useState(false);
  const handleReadBenchlingPostgresOpen = () =>
    setReadBenchlingPostgresOpen(true);
  const handleReadBenchlingPostgresClose = () =>
    setReadBenchlingPostgresOpen(false);

  const [readCosmosOpen, setReadCosmosOpen] = useState(false);
  const handleReadCosmosOpen = () => setReadCosmosOpen(true);
  const handleReadCosmosClose = () => setReadCosmosOpen(false);

  const [readApiOpen, setReadApiOpen] = useState(false);
  const handleReadApiOpen = () => setReadApiOpen(true);
  const handleReadApiClose = () => setReadApiOpen(false);

  const [readMySqlOpen, setReadMySqlOpen] = useState(false);
  const handleReadMySqlOpen = () => setReadMySqlOpen(true);
  const handleReadMySqlClose = () => setReadMySqlOpen(false);

  const [KPIOpen, setKPIOpen] = useState(false);
  const handleKPIOpen = () => setKPIOpen(true);
  const handleKPIClose = () => setKPIOpen(false);

  const [readSnowflakeOpen, setReadSnowflakeOpen] = useState(false);
  const handleReadSnowflakeOpen = () => setReadSnowflakeOpen(true);
  const handleReadSnowflakeClose = () => setReadSnowflakeOpen(false);

  const [readRealTimeOpen, setReadRealTimeOpen] = useState(false);
  const handleReadRealTimeOpen = () => setReadRealTimeOpen(true);
  const handleReadRealTimeClose = () => setReadRealTimeOpen(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const handleEmailOpen = () => setEmailOpen(true);
  const handleEmailClose = () => setEmailOpen(false);

  const [cleansingOpen, setCleansingOpen] = useState(false);
  const handleCleansingOpen = () => setCleansingOpen(true);
  const handleCleansingClose = () => setCleansingOpen(false);

  const [sqlOpen, setSqlOpen] = useState(false);
  const handleSqlOpen = () => setSqlOpen(true);
  const handleSqlClose = () => setSqlOpen(false);

  const [writeOpen, setWriteOpen] = useState(false);
  const handleWriteOpen = () => setWriteOpen(true);
  const handleWriteClose = () => setWriteOpen(false);

  const [writeToBenchlingOpen, setWriteToBenchlingOpen] = useState(false);
  const handleWriteToBenchlingOpen = () => setWriteToBenchlingOpen(true);
  const handleWriteToBenchlingClose = () => setWriteToBenchlingOpen(false);

  const [updateToBenchlingOpen, setUpdateToBenchlingOpen] = useState(false);
  const handleUpdateToBenchlingOpen = () => setUpdateToBenchlingOpen(true);
  const handleUpdateToBenchlingClose = () => setUpdateToBenchlingOpen(false);

  const [SnowFlakeOpen, setSnowFlakeOpen] = useState(false);
  const handleSnowFlakeOpen = () => setSnowFlakeOpen(true);
  const handleSnowFlakeClose = () => setSnowFlakeOpen(false);

  const [sourceOpen, setSourceOpen] = useState(false);
  const handleSourceOpen = () => setSourceOpen(true);
  const handleSourceClose = () => setSourceOpen(false);

  const [OpenSearchOpen, setOpenSearchOpen] = useState(false);
  const handleopenSearchOpen = () => setOpenSearchOpen(true);
  const handleOpenSearchClose = () => setOpenSearchOpen(false);

  const [WriteSFTPOpen, setWriteSFTPOpen] = useState(false);
  const handleWriteSFTPOpen = () => setWriteSFTPOpen(true);
  const handleWriteSFTPClose = () => setWriteSFTPOpen(false);

  const [DatabaseOpen, setDatabaseOpen] = useState(false);
  const handleDatabaseOpen = () => setDatabaseOpen(true);
  const handleDatabaseClose = () => setDatabaseOpen(false);

  const [aggregateOpen, setAggregateOpen] = useState(false);
  const handleAggregateOpen = () => setAggregateOpen(true);
  const handleAggregateClose = () => setAggregateOpen(false);

  const [C360Open, setC360Open] = useState(false);
  const handleC360Open = () => setC360Open(true);
  const handleC360Close = () => setC360Open(false);

  const [udfOpen, setUdfOpen] = useState(false);
  const handleUdfOpen = () => setUdfOpen(true);
  const handleUdfClose = () => setUdfOpen(false);

  const [coaleaseOpen, setCoaleaseOpen] = useState(false);
  const handleCoaleaseOpen = () => setCoaleaseOpen(true);
  const handleCoaleaseClose = () => setCoaleaseOpen(false);

  const [vicellOpen, setVicellOpen] = useState(false);
  const handleViCellOpen = () => setVicellOpen(true);
  const handleVicellClose = () => setVicellOpen(false);

  const [datatypeConvOpen, setDatatypeConvOpen] = useState(false);
  const handleDatatypeConvOpen = () => setDatatypeConvOpen(true);
  const handleDatatypeConvClose = () => setDatatypeConvOpen(false);

  const [concatOpen, setConcatOpen] = useState(false);
  const handleConcatOpen = () => setConcatOpen(true);
  const handleConcatClose = () => setConcatOpen(false);

  const [dateTimeOpen, setDateTimeOpen] = useState(false);
  const handleDateTimeOpen = () => setDateTimeOpen(true);
  const handleDateTimeClose = () => setDateTimeOpen(false);

  const [nullOpen, setNullOpen] = useState(false);
  const handleNullOpen = () => setNullOpen(true);
  const handleNullClose = () => setNullOpen(false);

  const [ingestionWriteOpen, setIngestionWriteOpen] = useState(false);
  const handleIngestionWriteOpen = () => setIngestionWriteOpen(true);
  const handleIngestionWriteClose = () => setIngestionWriteOpen(false);

  const [trainingOpen, setTrainingOpen] = useState(false);
  const handleTrainingOpen = () => setTrainingOpen(true);
  const handleTrainingClose = () => setTrainingOpen(false);

  const [predictionOpen, setPredictionOpen] = useState(false);
  const handlePredictionOpen = () => setPredictionOpen(true);
  const handlePredictionClose = () => setPredictionOpen(false);

  const [binningOpen, setBinningOpen] = useState(false);
  const handleBinningOpen = () => setBinningOpen(true);
  const handleBinningClose = () => setBinningOpen(false);

  const [profilingOpen, setProfilingOpen] = useState(false);
  const handleProfilingOpen = () => setProfilingOpen(true);
  const handleProfilingClose = () => setProfilingOpen(false);

  const [mathTransformationOpen, setMathTransformationOpen] = useState(false);
  const handleMathTransformationOpen = () => setMathTransformationOpen(true);
  const handleMathTransformationClose = () => setMathTransformationOpen(false);

  const [dataValidationOpen, setDataValidationOpen] = useState(false);
  const handleDataValidationOpen = () => setDataValidationOpen(true);
  const handleDataValidationClose = () => setDataValidationOpen(false);

  const [oneHotEncoderOpen, setOneHotEncoderOpen] = useState(false);
  const handleOneHotEncoderOpen = () => setOneHotEncoderOpen(true);
  const handleOneHotEncoderClose = () => setOneHotEncoderOpen(false);

  const [labelEncoderOpen, setLabelEncoderOpen] = useState(false);
  const handleLabelEncoderOpen = () => setLabelEncoderOpen(true);
  const handleLabelEncoderClose = () => setLabelEncoderOpen(false);

  const [isDataLineage, setIsDataLineage] = useState(false);
  const handleDataLineage = () => setIsDataLineage(!isDataLineage);
  const [dropOpen, setDropOpen] = useState(false);
  const handleDropOpen = () => setDropOpen(true);
  const handleDropClose = () => setDropOpen(false);

  const [imbalancedDataOpen, setImbalancedDataOpen] = useState(false);
  const handleImbalancedDataOpen = () => setImbalancedDataOpen(true);
  const handleImbalancedDataClose = () => setImbalancedDataOpen(false);

  const [outlierOpen, setOutlierOpen] = useState(false);
  const handleOutlierOpen = () => setOutlierOpen(true);
  const handleOutlierClose = () => setOutlierOpen(false);

  const [scalerOpen, setScalerOpen] = useState(false);
  const handleScalerOpen = () => setScalerOpen(true);
  const handleScalerClose = () => setScalerOpen(false);

  const [resampleOpen, setResampleOpen] = useState(false);
  const handleResampleOpen = () => setResampleOpen(true);
  const handleResampleClose = () => setResampleOpen(false);

  const [readMdmOpen, setReadMdmOpen] = useState(false);
  const handleReadMdmOpen = () => setReadMdmOpen(true);
  const handleReadMdmClose = () => setReadMdmOpen(false);

  const [fuzzyMatchOpen, setFuzzyMatchOpen] = useState(false);
  const handleFuzzyMatchOpen = () => setFuzzyMatchOpen(true);
  const handleFuzzyMatchClose = () => setFuzzyMatchOpen(false);

  const [ruleEngineOpen, setRuleEngineOpen] = useState(false);
  const handleRuleEngineOpen = () => setRuleEngineOpen(true);
  const handleRuleEngineClose = () => setRuleEngineOpen(false);

  const [manualMergeOpen, setManualMergeOpen] = useState(false);
  const handleManualMergeOpen = () => setManualMergeOpen(true);
  const handleManualMergeClose = () => setManualMergeOpen(false);

  const [custSegmentationOpen, setCustSegmentationOpen] = useState(false);
  const handleCustSegmentationOpen = () => setCustSegmentationOpen(true);
  const handleCustSegmentationClose = () => setCustSegmentationOpen(false);

  const [plotOpen, setPlotOpen] = useState(false);
  const handlePlotOpen = () => setPlotOpen(true);
  const handlePlotClose = () => setPlotOpen(false);

  const [analysisOpen, setAnalysisOpen] = useState(false);
  const handleAnalysisOpen = () => setAnalysisOpen(true);
  const handleAnalysisClose = () => setAnalysisOpen(false);

  const onConnect = useCallback((params) => {
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          type: "buttonedge",
          data: { onEdgesChange },
        },
        eds
      )
    );
  }, []);

  const onEdgeUpdate = (oldEdge, newConnection) =>
    setEdges((els) => updateEdge(oldEdge, newConnection, els));

  const onNodesChange = useCallback((changes) => {
    if (changes[0].type === "remove") {
      const getElementsId =
        JSON.parse(sessionStorage.getItem("saved_node")) || [];
      let updateNodes =
        JSON.parse(sessionStorage.getItem("updatedNodes")) || [];

      const updatedSavedNodes = getElementsId.filter(
        (el) => el !== changes[0].id
      );
      updateNodes = updateNodes.filter((el) => el !== changes[0].id);

      sessionStorage.setItem("saved_node", JSON.stringify(updatedSavedNodes));
      sessionStorage.setItem("updatedNodes", JSON.stringify(updateNodes));

      setNodes((ns) => applyNodeChanges(changes, ns));

      const getAllNodes = JSON.parse(sessionStorage.getItem("allNodes"));

      if (getAllNodes) {
        getAllNodes.forEach((el, i) => {
          if (el.nodeId === changes[0].id) {
            getAllNodes.splice(i, 1);
            sessionStorage.setItem("allNodes", JSON.stringify(getAllNodes));
          }
        });
      }

      const elemCount = parseInt(sessionStorage.getItem("elementCount"), 10);

      elemCount && sessionStorage.setItem("elementCount", elemCount - 1);

      setNodeName("");
      setNodeId("");
    } else {
      setNodes((ns) => applyNodeChanges(changes, ns));
    }
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((es) => applyEdgeChanges(changes, es));
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");
      const name = event.dataTransfer.getData("nodeName");
      // const className = event.dataTransfer.getData('className');

      // check if the dropped element is valid
      if (typeof type === "undefined" || !type) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const newNode = {
        id: getId(),
        type,
        position,
        data: { label: `${name}`, initNode: `${name}`, onNodesChange },
      };

      const elemCount = parseInt(sessionStorage.getItem("elementCount"), 10);

      elemCount
        ? sessionStorage.setItem("elementCount", elemCount + 1)
        : sessionStorage.setItem("elementCount", 1);

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance]
  );

  const onNodeClick = (event, node) => {
    if (nodeName || nodeId) {
      setNodeName("");
      setNodeId("");
    }

    setNodeId(node.id);
    setNodeName(node.data.initNode);

    // if (node.height === 174) {
    if (node.data.initNode === "Sort") {
      handleSortOpen();
    }

    if (node.data.initNode === "Filter") {
      handleFilterOpen();
    }

    if (node.data.initNode === "Union") {
      handleUnionOpen();
    }

    if (node.data.initNode === "Join") {
      handleJoinOpen();
    }
    if (node.data.initNode === "ViCell") {
      handleViCellOpen();
    }

    if (node.data.initNode === "Read") {
      handleReadOpen();
    }
    if (node.data.initNode === "ReadCSV") {
      handleReadCsvOpen();
    }
    if (node.data.initNode === "PREPROCESS_INSTRUMENT_FILE") {
      handlePreprocessInstrumentFileOpen();
    }
    if (node.data.initNode === "Instrument") {
      handleInstrumentOpen();
    }
    if (node.data.initNode === "Sql_Connector") {
      handleSqlConnectorOpen();
    }
    if (node.data.initNode === "CopyDoc") {
      handleCopyDocOpen();
    }
    if (node.data.initNode === "ReadPublishedData") {
      handleReadPublishedDataOpen();
    }
    if (node.data.initNode === "FileUploader") {
      handleFileUploaderOpen();
    }
    if (node.data.initNode === "ReadStreaming") {
      handleReadStreamingOpen();
    }
    if (node.data.initNode === "ReadDelimited") {
      handleReadDelimitedOpen();
    }
    if (node.data.initNode === "ReadOracle") {
      handleReadOracleOpen();
    }
    if (node.data.initNode === "ReadPostgres") {
      handleReadPostgresOpen();
    }
    if (node.data.initNode === "ReadBenchlingPostgres") {
      handleReadBenchlingPostgresOpen();
    }
    if (node.data.initNode === "ReadCosmos") {
      handleReadCosmosOpen();
    }
    if (node.data.initNode === "ReadApi") {
      handleReadApiOpen();
    }
    if (node.data.initNode === "ReadMySql") {
      handleReadMySqlOpen();
    }

    if (node.data.initNode === "ReadSnowflake") {
      handleReadSnowflakeOpen();
    }

    if (node.data.initNode === "Read Real-Time") {
      handleReadRealTimeOpen();
    }

    if (node.data.initNode === "Read MDM") {
      handleReadMdmOpen();
    }

    if (node.data.initNode === "Email") {
      handleEmailOpen();
    }

    if (node.data.initNode === "Data Cleansing") {
      handleCleansingOpen();
    }

    if (node.data.initNode === "KPI") {
      handleKPIOpen();
    }
    if (node.data.initNode === "ExecuteSQL") {
      handleSqlOpen();
    }

    if (node.data.initNode === "Write") {
      handleWriteOpen();
    }

    if (node.data.initNode === "WriteToBenchling") {
      handleWriteToBenchlingOpen();
    }
    if (node.data.initNode === "WriteToPostgres") {
      handleWritePostgresOpen();
    }

    if (node.data.initNode === "AutoRefresh") {
      handleUpdateToBenchlingOpen();
    }

    if (node.data.initNode === "SnowFlake") {
      handleSnowFlakeOpen();
    }

    if (node.data.initNode === "OpenSearch") {
      handleopenSearchOpen();
    }

    if (node.data.initNode === "WriteSFTP") {
      handleWriteSFTPOpen();
    }

    if (node.data.initNode === "Postgres") {
      handleDatabaseOpen();
    }

    if (node.data.initNode === "Aggregation") {
      handleAggregateOpen();
    }

    if (node.data.initNode === "C360") {
      handleC360Open();
    }

    if (node.data.initNode === "Udf") {
      handleUdfOpen();
    }

    if (node.data.initNode === "Coalesce") {
      handleCoaleaseOpen();
    }

    if (node.data.initNode === "Datatype Conversion") {
      handleDatatypeConvOpen();
    }

    if (node.data.initNode === "Concat") {
      handleConcatOpen();
    }

    if (node.data.initNode === "Date Time Feature") {
      handleDateTimeOpen();
    }

    if (node.data.initNode === "Handle Null") {
      handleNullOpen();
    }

    if (node.data.initNode === "Ingestion Write") {
      handleIngestionWriteOpen();
    }

    if (node.data.initNode === "Training") {
      handleTrainingOpen();
    }

    if (node.data.initNode === "Prediction") {
      handlePredictionOpen();
    }

    if (node.data.initNode === "Binning") {
      handleBinningOpen();
    }

    if (node.data.initNode === "Source") {
      handleSourceOpen();
    }

    if (node.data.initNode === "Profiling") {
      handleProfilingOpen();
    }

    if (node.data.initNode === "Math Transformation") {
      handleMathTransformationOpen();
    }

    if (node.data.initNode === "DataValidation") {
      handleDataValidationOpen();
    }

    if (node.data.initNode === "OneHot Encoder") {
      handleOneHotEncoderOpen();
    }

    if (node.data.initNode === "Label Encoder") {
      handleLabelEncoderOpen();
    }

    if (node.data.initNode === "Drop") {
      handleDropOpen();
    }

    if (node.data.initNode === "Imbalanced Data") {
      handleImbalancedDataOpen();
    }

    if (node.data.initNode === "Outlier") {
      handleOutlierOpen();
    }

    if (node.data.initNode === "Scaler") {
      handleScalerOpen();
    }

    if (node.data.initNode === "Resample") {
      handleResampleOpen();
    }

    if (node.data.initNode === "Fuzzy Match") {
      handleFuzzyMatchOpen();
    }

    if (node.data.initNode === "Data Cleaning & Validation") {
      handleRuleEngineOpen();
    }

    if (node.data.initNode === "Match & Merge") {
      handleManualMergeOpen();
    }

    if (node.data.initNode === "Customer Segmentation") {
      handleCustSegmentationOpen();
    }
    if (node.data.initNode === "Plot") {
      handlePlotOpen();
    }

    if (node.data.initNode === "Analysis") {
      handleAnalysisOpen();
    }
  };

  const changeNodeName = (newNode) => {
    newNode.forEach((el) => {
      el.data.onNodesChange = onNodesChange;
    });
    setNodes(newNode);
  };

  const resetElements = () => {
    setNodes([]);
  };

  useEffect(() => {
    const fetchNodes = JSON.parse(sessionStorage.getItem("node"));
    const fetchEdges = JSON.parse(sessionStorage.getItem("edges"));

    if (fetchEdges && fetchNodes) {
      fetchNodes.forEach((el) => {
        el.data.onNodesChange = onNodesChange;
      });
      fetchEdges.forEach((el) => {
        el.data.onEdgesChange = onEdgesChange;
      });

      setNodes(fetchNodes);
      setEdges(fetchEdges);
    }
  }, []);

  const getNodeData = JSON.parse(sessionStorage.getItem("saved_node"));

  useEffect(() => {
    setIsDisabled(false);

    if (
      nodes.length === 0 ||
      getNodeData?.length === 0 ||
      getNodeData === null
    ) {
      setIsDisabled(true);
    }
  }, [isDisabled, nodes, getNodeData]);

  const submitDataProcessor = async () => {
    console.log("submit");
    props.setLoading(true);
    try {
      if (nodes.length === 0) {
        props.setLoading(false);
      }
      const newEdges = [];

      edges.forEach((el) => {
        const source = nodes.filter((item) => item.id === el.source);
        const target = nodes.filter((item) => item.id === el.target);

        if (source.length > 0 && target.length > 0) {
          newEdges.push(el);
        }
      });

      const getAllDataFromLocalStorage = JSON.parse(
        sessionStorage.getItem("allNodes")
      );

      getAllDataFromLocalStorage.map((item) => {
        nodes.forEach((el) => {
          if (item.nodeId === el.id) {
            item.y_axis = parseInt(el.position.x, 10);
          }
        });
      });

      getAllDataFromLocalStorage.sort((a, b) => a.y_axis - b.y_axis);

      const getNodeData = JSON.parse(sessionStorage.getItem("saved_node"));

      const getElementCount =
        parseInt(sessionStorage.getItem("elementCount"), 10) || "";

      if (getNodeData.length === getElementCount) {
        let count = 1;
        getAllDataFromLocalStorage.forEach((el) => {
          el.formField.step_no = count;
          count++;
        });

        getAllDataFromLocalStorage.sort(
          (a, b) => a.formField.step_no - b.formField.step_no
        );

        const tempFormData = getAllDataFromLocalStorage;

        tempFormData.forEach((el) => {
          if (el.nodeName === "Data Cleansing") {
            el.cleansingRules = el.cleansingRules.filter((el) => {
              return !el.deleted;
            });
          }
        });

        tempFormData.forEach((el) => {
          if (el.nodeName === "Data Cleansing") {
            el.initial_rules = el.cleansingRules;
          }
        });

        const createFormData = {
          workflow_id: workflow.workflow_id,
          execution_mode: workflow.execution_mode,
          form_data: tempFormData,
          nodes,
          edges: newEdges,
          userId: props.userData.userId,
        };
        const createForm = await createDataProcessorApi(axios, createFormData);

        if (createForm.status === 201) {
          sessionStorage.removeItem("allNodes");
          sessionStorage.removeItem("node");
          sessionStorage.removeItem("edges");
          sessionStorage.removeItem("nodeStatus");
          sessionStorage.removeItem("elementCount");
          sessionStorage.removeItem("saved_node");

          enqueueSnackbar("Steps and Data are saved!", {
            variant: "success",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });

          resetElements();
        }
      }
    } catch (error) {
      console.log(error);
      if (error.response.status === 403) {
        enqueueSnackbar("You have only Read Permission !!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
      if (error.response.status === 500) {
        enqueueSnackbar("Something went wrong !!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    }
    props.setLoading(false);
  };

  ref.current = submitDataProcessor;
  const proOptions = {
    hideAttribution: true,
  };

  return (
    <>
      <Paper
        elevation={5}
        style={{
          height: "100%",
          overflow: "visible",
          display: "flex",
          flexDirection: "row",
        }}
        className="react-flow-wrappper"
      >
        <div className="node-sec">
          <NodesSection processType={props.processType} />
        </div>
        <div className="dndflow">
          <ReactFlowProvider>
            {nodeName === "Write" && nodes && (
              <NodeModalWrite
                key={nodeId}
                nodeId={nodeId}
                changeNodeName={changeNodeName}
                nodeName={nodeName}
                nodes={nodes}
                edges={edges}
                open={writeOpen}
                setNodes={setNodes}
                handleClose={handleWriteClose}
                workflow={workflow}
              />
            )}
            {nodeName === "WriteToBenchling" && nodes && (
              <NodeModalWriteToBenchling
                key={nodeId}
                nodeId={nodeId}
                changeNodeName={changeNodeName}
                nodeName={nodeName}
                nodes={nodes}
                edges={edges}
                open={writeToBenchlingOpen}
                setNodes={setNodes}
                handleClose={handleWriteToBenchlingClose}
                workflow={workflow}
              />
            )}
            {nodeName === "WriteToPostgres" && nodes && (
              <NodeModal_WriteToPostgres
                key={nodeId}
                nodeId={nodeId}
                changeNodeName={changeNodeName}
                nodeName={nodeName}
                nodes={nodes}
                edges={edges}
                open={writePostgresOpen}
                setNodes={setNodes}
                handleClose={handleWritePostgresClose}
                workflow={workflow}
              />
            )}

            {nodeName === "Ingestion Write" && nodes && (
              <NodeModalIngestionWrite
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={ingestionWriteOpen}
                setNodes={setNodes}
                handleClose={handleIngestionWriteClose}
                workflow={workflow}
              />
            )}
            {nodeName === "Source" && nodes && (
              <NodeModalSource
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={sourceOpen}
                setNodes={setNodes}
                handleClose={handleSourceClose}
                processType={props.processType}
                user_id={props.userData.userId}
                workflow={workflow}
              />
            )}
            {nodeName === "ReadCSV" && nodes && (
              <NodeModalReadCSV
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={readCsvOpen}
                setNodes={setNodes}
                handleClose={handleReadCsvClose}
                workflow={workflow}
              />
            )}
            {nodeName === "ReadPostgres" && nodes && (
              <NodeModalReadPostgres
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={readPostgresOpen}
                setNodes={setNodes}
                handleClose={handleReadPostgresClose}
                workflow={workflow}
              />
            )}
            {nodeName === "ReadBenchlingPostgres" && nodes && (
              <NodeModalReadBenchlingPostgres
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={readBenchlingPostgresOpen}
                setNodes={setNodes}
                handleClose={handleReadBenchlingPostgresClose}
                workflow={workflow}
              />
            )}
            {nodeName === "ReadOracle" && nodes && (
              <NodeModalReadOracle
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={readOracleOpen}
                setNodes={setNodes}
                handleClose={handleReadOracleClose}
                workflow={workflow}
              />
            )}
            {nodeName === "PREPROCESS_INSTRUMENT_FILE" && nodes && (
              <NodeModalPreprocessInstrumentFile
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={preprocessInstrumentFileOpen}
                setNodes={setNodes}
                handleClose={handlePreprocessInstrumentFileClose}
                workflow={workflow}
                userData={userData}
              />
            )}
            {nodeName === "Instrument" && nodes && (
              <NodeModalInstrument
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={instrumentOpen}
                setNodes={setNodes}
                handleClose={handleInstrumentClose}
                workflow={workflow}
              />
            )}
            {nodeName === "Sql_Connector" && nodes && (
              <NodeModalSqlConnector
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={sqlConnectorOpen}
                setNodes={setNodes}
                handleClose={handleSqlConnectorClose}
                workflow={workflow}
              />
            )}
            {nodeName === "ExecuteSQL" && nodes && (
              <NodeModalExecuteSql
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={sqlOpen}
                setNodes={setNodes}
                handleClose={handleSqlClose}
              />
            )}
            {nodeName === "Join" && nodes && (
              <NodeModalJoin
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={joinOpen}
                setNodes={setNodes}
                handleClose={handleJoinClose}
              />
            )}

            {nodeName === "Sort" && nodes && (
              <NodePropertiesSort
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={sortOpen}
                setNodes={setNodes}
                handleClose={handleSortClose}
              />
            )}

            {nodeName === "Coalesce" && nodes && (
              <NodeModalCoalease
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={coaleaseOpen}
                setNodes={setNodes}
                handleClose={handleCoaleaseClose}
              />
            )}

            {nodeName === "Filter" && nodes && (
              <NodePropertiesFilter
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={filterOpen}
                setNodes={setNodes}
                handleClose={handleFilterClose}
              />
            )}

            {nodeName === "Aggregation" && nodes && (
              <NodeModalAggregate
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={aggregateOpen}
                setNodes={setNodes}
                handleClose={handleAggregateClose}
              />
            )}

            {nodeName === "Union" && nodes && (
              <NodeModalUnion
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={unionOpen}
                setNodes={setNodes}
                handleClose={handleUnionClose}
              />
            )}

            {nodeName === "Email" && nodes && (
              <NodeModalEmail
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={emailOpen}
                setNodes={setNodes}
                handleClose={handleEmailClose}
              />
            )}
            {nodeName === "Profiling" && nodes && (
              <NodeModalProfiling
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={profilingOpen}
                setNodes={setNodes}
                handleClose={handleProfilingClose}
              />
            )}
            {nodeName === "DataValidation" && nodes && (
              <NodeModalDataValidation
                key={nodeId}
                nodeId={nodeId}
                nodeName={nodeName}
                changeNodeName={changeNodeName}
                nodes={nodes}
                edges={edges}
                open={dataValidationOpen}
                setNodes={setNodes}
                handleClose={handleDataValidationClose}
                workflow={workflow}
              />
            )}
            <div className="reactflow-wrapper" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodeClick={onNodeClick}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onEdgeUpdate={onEdgeUpdate}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                proOptions={proOptions}
                fitView
                attributionPosition="bottom-left"
              >
                <Controls position="top-left" />
                <Background variant="cross" />
              </ReactFlow>
            </div>
          </ReactFlowProvider>
          {/* <DataLineageGraph open={isDataLineage} handleClose={handleDataLineage} /> */}
        </div>
      </Paper>
    </>
  );
});

export default ReactFlowPannel;
